/**
 * Drill Schedules API service for Whakahaere Safety Management System
 *
 * Targets the schema-canonical `/api/v2/drills/schedules/` route with the
 * REAL backend field contract (`name`, `school`, `scheduled_date` ISO
 * datetime, `recurrence_rule`, `status`); there is no `/api/v2/drill-schedule/`
 * route on the backend.
 *
 * Also carries the automation config (stored server-side in the
 * schedule's `data` JSONB): `notify_thread_ids` (chat groups notified on
 * reminders + start), `reminders` (multi-select milestones; empty = never),
 * and `requires_approval` (drill auto-run gated by the alert-approval pen).
 */

import { client } from '../lib/http-client';

export type DrillScheduleStatus =
  | 'scheduled'
  | 'started'
  | 'completed'
  | 'cancelled'
  | 'pending_approval';

export type DrillReminderMilestone =
  | 'prior_month'
  | 'prior_week'
  | 'prior_day'
  | 'same_day';

export const DRILL_REMINDER_MILESTONES: {
  value: DrillReminderMilestone;
  label: string;
}[] = [
  { value: 'prior_month', label: 'Prior month' },
  { value: 'prior_week', label: 'Prior week' },
  { value: 'prior_day', label: 'Prior day' },
  { value: 'same_day', label: 'Same day' },
];

/** UI-level recurrence presets; the wire format is an iCal RRULE. */
export type RecurrencePattern =
  | 'once'
  | 'weekly'
  | 'monthly'
  | 'quarterly'
  | 'annually'
  | 'custom';

/** Wire shape of `recurrence_rule` (one of the shapes the backend accepts). */
export type RecurrenceRule = { rrule: string } | null;

const PATTERN_TO_RRULE: Record<
  Exclude<RecurrencePattern, 'once' | 'custom'>,
  string
> = {
  weekly: 'FREQ=WEEKLY',
  monthly: 'FREQ=MONTHLY',
  quarterly: 'FREQ=MONTHLY;INTERVAL=3',
  annually: 'FREQ=YEARLY',
};

export function recurrenceRuleFromPattern(
  pattern: RecurrencePattern,
): RecurrenceRule {
  if (pattern === 'once' || pattern === 'custom') return null;
  return { rrule: PATTERN_TO_RRULE[pattern] };
}

/**
 * Map a wire `recurrence_rule` back to a UI preset. Unrecognized (but
 * present) rules — e.g. hand-authored RRULEs from the admin — render as
 * `'custom'`; callers must preserve the original rule when the user leaves
 * the recurrence untouched.
 */
export function patternFromRecurrenceRule(
  rule: RecurrenceRule | string | null | undefined,
): RecurrencePattern {
  if (!rule) return 'once';
  const text = (typeof rule === 'string' ? rule : rule.rrule || '')
    .trim()
    .toUpperCase();
  if (!text) return 'once';
  const match = (
    Object.entries(PATTERN_TO_RRULE) as [RecurrencePattern, string][]
  ).find(([, rrule]) => text === rrule || text === `RRULE:${rrule}`);
  return match ? match[0] : 'custom';
}

export interface DrillSchedule {
  id: number;
  school: number;
  school_name?: string;
  drill_type: string;
  drill_type_display?: string;
  alert_type: number;
  alert_type_name?: string;
  name: string;
  description: string;
  /** ISO-8601 datetime — the exact moment the drill auto-runs. */
  scheduled_date: string;
  recurrence_rule: RecurrenceRule | string | null;
  notification_minutes_before?: number;
  /** Chat group (thread) ids notified on reminders + start. */
  notify_thread_ids: number[];
  /** Reminder milestones; empty array = never. */
  reminders: DrillReminderMilestone[];
  /** Auto-run must pass the alert-approval pen before starting. */
  requires_approval: boolean;
  /** Start the drill's internal Alert silently (no audible alerts). */
  is_silent: boolean;
  status: DrillScheduleStatus;
  status_display?: string;
  created_by?: number | null;
  created_by_email?: string;
  created?: string;
  updated?: string;
}

/** Fields a client may write on create/update. */
export type DrillSchedulePayload = Partial<
  Pick<
    DrillSchedule,
    | 'school'
    | 'drill_type'
    | 'alert_type'
    | 'name'
    | 'description'
    | 'scheduled_date'
    | 'recurrence_rule'
    | 'notify_thread_ids'
    | 'reminders'
    | 'requires_approval'
    | 'is_silent'
    | 'status'
  >
>;

export interface DrillScheduleListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: DrillSchedule[];
}

export interface DrillScheduleFilters {
  search?: string;
  school?: number;
  drill_type?: string;
  status?: DrillScheduleStatus;
  page?: number;
  page_size?: number;
  ordering?: string;
}

function buildSearch(filters: DrillScheduleFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.search) params.append('search', filters.search);
  if (filters.school) params.append('school', String(filters.school));
  if (filters.drill_type) params.append('drill_type', filters.drill_type);
  if (filters.status) params.append('status', filters.status);
  if (filters.page) params.append('page', String(filters.page));
  if (filters.page_size) params.append('page_size', String(filters.page_size));
  if (filters.ordering) params.append('ordering', filters.ordering);
  return params;
}

export async function getDrillSchedules(
  filters: DrillScheduleFilters = {},
): Promise<DrillScheduleListResponse> {
  const params = buildSearch(filters);
  const qs = params.toString();
  const response = await client.ky.get(
    `api/v2/drills/schedules/${qs ? `?${qs}` : ''}`,
  );
  return (await response.json()) as DrillScheduleListResponse;
}

export async function getDrillScheduleById(id: number): Promise<DrillSchedule> {
  const response = await client.ky.get(`api/v2/drills/schedules/${id}/`);
  return (await response.json()) as DrillSchedule;
}

export async function createDrillSchedule(
  data: DrillSchedulePayload,
): Promise<DrillSchedule> {
  const response = await client.ky.post('api/v2/drills/schedules/', {
    json: data,
  });
  return (await response.json()) as DrillSchedule;
}

export async function updateDrillSchedule(
  id: number,
  data: DrillSchedulePayload,
): Promise<DrillSchedule> {
  const response = await client.ky.patch(`api/v2/drills/schedules/${id}/`, {
    json: data,
  });
  return (await response.json()) as DrillSchedule;
}

export async function deleteDrillSchedule(id: number): Promise<void> {
  await client.ky.delete(`api/v2/drills/schedules/${id}/`);
}
